import { useState, useEffect, useCallback } from "react";
import {
  collection, addDoc, deleteDoc, updateDoc,
  doc, query, orderBy, onSnapshot, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

// Ficha de cliente (solo modo negocio): nombre, DNI, dirección, email y 3
// campos libres para lo que haga falta (referencia, etc.). Vive en el
// mismo workspace que el resto de datos de negocio — así, si la cuenta es
// compartida, todos los que tengan acceso ven la misma lista de clientes.
export function useClientes() {
  const { user, profile } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const workspaceId = profile?.activeWorkspaceId || profile?.personalWorkspaceId || null;
  const clientesRef = useCallback(() => {
    if (workspaceId) return collection(db, "workspaces", workspaceId, "clientes");
    return collection(db, "users", user.uid, "clientes");
  }, [workspaceId, user]);
  const clienteDoc = useCallback((id) => {
    if (workspaceId) return doc(db, "workspaces", workspaceId, "clientes", id);
    return doc(db, "users", user.uid, "clientes", id);
  }, [workspaceId, user]);

  useEffect(() => {
    if (!user) return;
    const ref = clientesRef();
    const q = query(ref, orderBy("nombre", "asc"));
    const unsub = onSnapshot(q, snap => {
      setClientes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
      setError(null);
    }, err => {
      // Sin este segundo callback, un fallo de permisos en Firestore
      // (reglas de seguridad, workspace inaccesible, etc.) dejaba
      // "loading" en true para siempre, sin ningún aviso — la página
      // se veía completamente en blanco sin explicación.
      console.error("Error cargando clientes:", err);
      setError(err.message || String(err));
      setLoading(false);
    });
    return unsub;
  }, [user, workspaceId]);

  async function addCliente(data) {
    if (!user) {
      // Antes, sin usuario, esto devolvía "undefined" sin avisar — quien
      // llama (p.ej. el "Guardar" rápido de un contacto nuevo desde
      // Añadir Entrada) no se enteraba de que no se había guardado nada:
      // el formulario se cerraba igualmente, como si hubiera ido bien,
      // aunque no se hubiera creado ningún contacto de verdad. Ahora se
      // lanza un error para que quien llama pueda mostrarlo y el
      // problema no pase desapercibido en silencio.
      throw new Error("No hay sesión iniciada — no se puede guardar el contacto.");
    }
    try {
      const created = await addDoc(clientesRef(), {
        ...data, addedBy: user.uid, createdAt: serverTimestamp(),
      });
      return created.id;
    } catch (err) {
      // Igual que en el listener de arriba: se deja constancia en
      // consola del motivo real (permisos de Firestore, workspace
      // inaccesible, etc.) para poder diagnosticarlo si vuelve a pasar,
      // y se relanza para que quien llama pueda avisar al usuario en vez
      // de quedarse callado como si hubiera ido bien.
      console.error("Error al crear el contacto:", err);
      throw err;
    }
  }

  async function updateCliente(id, data) {
    try {
      await updateDoc(clienteDoc(id), data);
    } catch (err) {
      console.error("Error al actualizar el contacto:", err);
      throw err;
    }
  }

  async function deleteCliente(id) {
    try {
      await deleteDoc(clienteDoc(id));
    } catch (err) {
      console.error("Error al borrar el contacto:", err);
      throw err;
    }
  }

  return {
    clientes, loading, error,
    addCliente, updateCliente, deleteCliente,
  };
}
